<template>
  <div>
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push(`/${module}`)"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn  style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="readonly ? 'grey-5' : 'ctrl-turquoise'"
          :label="readonly ? $store.state.stringsStore.strings.action.edit : $store.state.stringsStore.strings.action.save"
          @click="save()"
          :icon-right="readonly ? 'edit' : 'save'"
        />
        <q-btn v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :disabled="readonly"
          :label="$store.state.stringsStore.strings.action.delete"
          align="right"
          @click="deleteItem()"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">
      <!-- Left -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              Details
            </p>

            <div class="card-block">
              <div class="row">
                <q-datetime
                  :disable="readonly"
                  class="full-width q-mb-md"
                  :float-label="getString('module.holidays.date')"
                  v-model="data.date" type="date"
                />

                <q-input
                  :disable="readonly"
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('module.holidays.reason')"
                  v-model="data.reason"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- End left -->
      <!-- Begin right(notification) -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row q-mb-sm">

          <div class="full-width">
            <p class="card-title">
              {{ getString('module.holidays.notification') }}
            </p>

            <div class="card-block">
              <div class="row">
                <q-checkbox
                  class="full-width q-mb-md holidays-checkbox"
                  :disable="readonly"
                  :readonly="readonly"
                  :label="getString('module.holidays.show_notification')"
                  v-model="data.notification"
                />

                <template v-if="data.notification">
                  <q-select
                    class="full-width q-mb-md"
                    :float-label="getString('module.holidays.days_before_notification')"
                    :options="dayOptions"
                    :disable="readonly"
                    :readonly="readonly"
                    v-model="data.daysBeforeNotification"
                  />
                  <div class="q-if-label q-if-label-above">
                    <div class="q-if-label-inner ellipsis">
                      {{ getString('module.holidays.notification_text') }}
                    </div>
                  </div>
                  <editor
                    tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
                    :init="tinyConfig()"
                    :disabled="readonly"
                    ref="tm"
                    v-model="data.notificationText"
                  ></editor>
                </template>
              </div>
            </div>

          </div>
        </div>
      </div>
      <!-- End right -->
    </div>
  </div>
</template>

<script>
import moment from 'moment';

// Mixins
import getString from '@/helpers/GetStrings';
import suggestUrl from '@/mixins/suggestUrl';
import saveChanges from '@/mixins/detailViews/saveChanges';

const Editor = require('@tinymce/tinymce-vue').default;

export default {
  mixins: [
    suggestUrl,
    saveChanges,
  ],
  components: {
    Editor,
  },
  data() {
    const dayOptions = [1, 2, 3, 4, 5, 6, 7].map(day => ({
      value: day,
      label: `${day}`,
    }));

    return {
      readonly: true,
      imageModal: false,
      containsErrors: true,
      module: 'holidays',
      endpoint: 'holidays',
      data: {
        id: null,
        date: moment().format('YYYY-MM-DD'),
        reason: '',
        notification: true,
        notificationText: null,
        daysBeforeNotification: null,
      },
      labelSet: false,
      dayOptions,
    };
  },

  methods: {
    getString,
    deleteItem() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: this.getString('module.library.delete.title.single'),
        ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
        cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
      }).then(() => {
        // delete call
        this.axios.delete(`${this.endpoint}/${this.data.id}`)
          .then(() => {
            // Refresh data to see the result
            this.$q.notify({
              message: this.$store.state.stringsStore.strings.module.library.delete.deleted.single,
              position: 'top-right',
              timeout: 1000,
              color: 'positive',
            });
            this.$router.push('/holidays');
          })
          .catch((error) => {
            // Error
            console.warn(error);
          });
      }).catch(() => {
        console.warn('Not removed');
      });
    },

    requestParams() {
      const requiredFields = {
        date: moment(this.data.date).format('YYYY-MM-DD'),
        reason: this.data.reason,
        notification: this.data.notification ? 1 : 0,
      };

      const optional = {
        reason: this.data.reason,
        id: this.$route.params && this.$route.params.id,
        notification_text: this.data.notificationText,
        days_before_notification: this.data.daysBeforeNotification,
      };

      // If a value is null, undefined or empty, remove it from fields
      for (const [key, value] of Object.entries(optional)) {
        if (!value) delete optional[key];
      }

      // Join required and optional
      const fields = Object.assign(requiredFields, optional);

      return fields;
    },
    save() {
      if (this.readonly === true) {
        this.readonly = false;
        return;
      }
      this.saveChanges();
      this.readonly = !this.readonly;
    },

    tinyConfig() {
      return {
        branding: false,
        browser_spellcheck: true,
        plugins: ['link', 'lists'],
        toolbar: [
          'undo redo | bullist numlist | styleselect | bold italic | link alignleft aligncenter alignright | code',
        ],

        setup: (editor) => {
          editor.addButton('imageSelect', {
            tooltip: 'Select an image from the library',
            icon: 'books',
            onclick: () => {
              this.insertImage(editor);
            },
          });
        },
        pagebreak_split_block: true,
      };
    },
  },

  mounted() {
    if (this.$route.params.id) {
      this.axios.get(`holidays/${this.$route.params.id}`).then((res) => {
        const holiday = res.data.holidays;

        this.data = {
          id: holiday.id,
          date: holiday.date,
          reason: holiday.reason,
          notification: holiday.notification,
          daysBeforeNotification: holiday.days_before_notification,
          notificationText: holiday.notification_text,
        };
      });
    } else {
      this.readonly = false;
    }
  },
  watch: {
    'data.notification': function (val) {
      if (val === false) {
        this.data.notificationText = null;
        this.data.daysBeforeNotification = null;
      }
    },
  },
};
</script>

<style lang="stylus" scoped>

</style>
